// Keeping the Projects & services list clean: spot lines that are really bank /
// tax / label text read from an invoice, duplicates, and what sounds recurring.

import { num, projectLine } from "./utils";

export const normName = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/\bkeywords?\b/g, "keyword")
    .replace(/[^a-z0-9]/g, "");

// Words that start a label / footer line on an invoice, never a project.
const LABEL =
  /^(account|a\/c|bank|iban|swift|bic|ifsc|sort\s*code|routing|beneficiary|branch|payee|pay(ment|able)?|currency|(sub\s*)?total|balance|amount|tax|gst|vat|igst|cgst|sgst|discount|round(ing)?\s*off|terms|notes?|thank|signature|authori[sz]ed|invoice|due|date|bill(ed)?\s*to|ship(ped)?\s*to|gstin|pan|lut|arn|e-?mail|phone|tel|mobile|website|address|place\s*of\s*supply|reference|ref|remarks|received|credit|paid|upi|paypal|wise|revolut)\b/i;

// Why a project/service looks like junk read from an invoice, or "" if it looks fine.
export function junkReason(p) {
  const text = projectLine(p).trim();
  const rate = num(p.rate);
  if (!text) return "Empty name";
  if (/^[^:]{2,40}:\s*\S/.test(text) && LABEL.test(text)) return "Looks like an invoice label (e.g. bank / account details)";
  if (LABEL.test(text) && rate === 0) return "Looks like an invoice label with no price";
  if (/\b[A-Z]{2}\d{2}[A-Z0-9]{10,}\b/.test(text.replace(/\s/g, "")) || /\b\d{8,}\b/.test(text)) {
    if (rate === 0) return "Looks like an account / phone number";
  }
  if (!/[a-z]/i.test(text)) return "No words in the name";
  return "";
}

// Monthly work: SEO keywords, local SEO / GMB, ads management, social, maintenance…
export const RECURRING_RE = /\b(keywords?|kws?|seo|gmb|gbp|local|geo|maintenance|retainer|monthly|per\s*month|management|ads|ppc|social|smm|posting|posts|hosting|support|care\s*plan|subscription|backlinks?)\b/i;
export const looksRecurring = (text) => RECURRING_RE.test(String(text || ""));

// Same name twice. Only true duplicates are suggested for removal: extra copies
// for the same client, and "any client" copies of something a client already has.
// Two different clients with the same package ("5 Keywords") are not duplicates.
export function duplicateGroups(projects) {
  const byName = new Map();
  for (const p of projects) {
    const k = normName(projectLine(p));
    if (k) byName.set(k, [...(byName.get(k) || []), p]);
  }
  const out = [];
  for (const list of byName.values()) {
    if (list.length < 2) continue;
    const byClient = new Map();
    for (const p of list) byClient.set(p.clientId || "", [...(byClient.get(p.clientId || "") || []), p]);
    const remove = [];
    const keep = [];
    for (const [cid, arr] of byClient) {
      const [best, ...rest] = [...arr].sort((x, y) => score(y) - score(x));
      if (cid) {
        keep.push(best);
        remove.push(...rest);
      } else if (byClient.size > 1) remove.push(...arr); // generic copy of a client's project
      else {
        keep.push(best);
        remove.push(...rest);
      }
    }
    if (remove.length) out.push({ keep, remove, items: [...keep, ...remove] });
  }
  return out;
}

// The most useful copy: active, priced, monthly, has a start date.
const score = (p) => (p.status === "active" ? 4 : 0) + (num(p.rate) > 0 ? 2 : 0) + (p.billing === "monthly" ? 1 : 0) + (p.startDate ? 0.5 : 0);

export function listIssues(projects) {
  const junk = projects.map((p) => ({ p, reason: junkReason(p) })).filter((x) => x.reason);
  const junkIds = new Set(junk.map((x) => x.p.id));
  const dups = duplicateGroups(projects.filter((p) => !junkIds.has(p.id)));
  return { junk, dups, count: junk.length + dups.reduce((n, g) => n + g.remove.length, 0) };
}
